// Helper functions to check permissions from authState
import { authState } from '@/scripts/services/authService'

// Helper function to check if user has a specific permission in an organization
// Will always return true if user is the owner of the organization or a superuser
export const hasPermission = (organizationId, permission) => {
  return isSuperuser() || 
    (organizationId && 
      (isOwner(organizationId) || authState.permissions.value.includes(`${organizationId}:${permission}`)))
}

// Helper function to check if user is owner of an organization
export const isOwner = (organizationId) => {
  return authState.permissions.value.includes(`${organizationId}:organization:owner`)
}

export const isProtectedRole = (roleName) => {
  return roleName.toLowerCase() === 'owner'
}

// Helper function to check if user is superuser
export const isSuperuser = () => {
  return authState.permissions.value.includes(`system:superuser`)
}

// Organization Permissions
export const canEditOrganization = (organizationId) => {
  return hasPermission(organizationId, 'organization:manage')
}

// User Permissions
export const canViewUsers = (organizationId) => {
  return hasPermission(organizationId, 'user:view')
}

export const canInviteUsers = (organizationId) => {
  return hasPermission(organizationId, 'user:manage')
}

export const canEditUsers = (organizationId) => {
  return hasPermission(organizationId, 'user:manage')
}

export const canRemoveUser = canEditUsers // Same permissions as edit user

// Channel Permissions
export const canCreateChannel = (organizationId) => {
  return hasPermission(organizationId, 'channel:manage')
}

export const canEditChannel = canCreateChannel // Same permissions as create
export const canDeleteChannel = canCreateChannel // Same permissions as create

export const canViewChannelPrivateContents = (organizationId) => {
  return hasPermission(organizationId, 'channel:view')
}

export const canViewSeries = canViewChannelPrivateContents // Same permissions as channel:view
export const canViewSeason = canViewChannelPrivateContents // Same permissions as channel:view

export const canCreateSeries = canCreateChannel // Same permissions as channel:manage
export const canEditSeries = canCreateChannel // Same permissions as channel:manage
export const canDeleteSeries = canCreateChannel // Same permissions as channel:manage

// Video Permissions
export const canViewVideo = (organizationId) => {
  return canViewChannelPrivateContents(organizationId) ||
    hasPermission(organizationId, 'video:view')
}

export const canCreateVideo = (organizationId) => {
  return hasPermission(organizationId, 'video:create')
}

export const canEncodeVideo = (videoFile) => {
  return videoFile && videoFile.stream_status === 'NotStarted' && canCreateVideo(videoFile.organization_id)
}

export const canEditVideo = (organizationId) => {
  return hasPermission(organizationId, 'video:edit')
}

export const canDeleteVideo = (organizationId) => {
  return hasPermission(organizationId, 'video:delete')
}

export const canReviewVideo = (organizationId) => {
  return hasPermission(organizationId, 'video:review')
}

export const canShareVideo = (organizationId) => {
  return hasPermission(organizationId, 'video:create')
}

export const rolePriority = {
  'Owner': 0,
  'Admin': 1,
  'Editor': 2,
  'Reviewer': 3,
  'Viewer': 4,
  'Guest': 5
};

export function sortRoles(roles) {
  return [...roles].sort((a, b) => {
    // roles could be a list of strings, or a list of role objects
    const roleNameA = typeof a === 'string' ? a : (a.name || '');
    const roleNameB = typeof b === 'string' ? b : (b.name || '');
    
    const priorityA = rolePriority[roleNameA] !== undefined ? rolePriority[roleNameA] : 999;
    const priorityB = rolePriority[roleNameB] !== undefined ? rolePriority[roleNameB] : 999;
    return priorityA - priorityB;
  });
}

export function isSystemPermission(permission) {
  const permissionName = permission?.name || '';
  return permissionName.startsWith('system:');
}

export const permissionDomainPriority = {
  'system': 1000,
  'organization': 900,
  'user': 800,
  'channel': 700,
  'video': 600,
};

export const permissionActionPriority = {
  'superuser': 90,
  'owner': 80,
  'manage': 70,
  'create': 60,
  'edit': 50,
  'delete': 40,
  'review': 30,
  'view': 20,
};

export function sortPermissions(permissions) {
  return [...permissions].sort((a, b) => {
    const permissionA = a?.name || '';
    const permissionB = b?.name || '';
    
    const [domainA, actionA] = permissionA.split(':');
    const [domainB, actionB] = permissionB.split(':');
    
    let priorityA = (permissionDomainPriority[domainA] || 0) + (permissionActionPriority[actionA] || 0);
    let priorityB = (permissionDomainPriority[domainB] || 0) + (permissionActionPriority[actionB] || 0);
    
    if (priorityA !== priorityB) return priorityB - priorityA;
    
    return permissionA.localeCompare(permissionB);
  });
}
